import { Box, FormControl, InputLabel, MenuItem, Select, IconButton, Button, Collapse } from '@mui/material';
import CustomContainer from '../../components/CustomContainer';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import StatusChip from '../../components/StatusChip';
import AppCard from '../../components/ui/AppCard';
import SearchField from '../../components/ui/SearchField';
import { DataCell, DataList, DataRow } from '../../components/ui/DataList';
import { Link } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getOrders } from '../../helpers/httpRequests/orders';
import LoadingComponent from '../../components/LoadingComponent';
import { useMemo, useState } from 'react';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { orderStatusInfo } from '../../utils/variables';
import { FaFileCsv, FaFilter, FaPlus, FaRegImage } from 'react-icons/fa6';
import { Avatar, Pagination, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { getCategories } from '../../helpers/httpRequests/product_props';
import { formatOptions, handleDownloadOrders, moneyFormat } from '../../utils/functions';

const ITEMS_PER_PAGE = 8;

const columns = [
    { label: 'Pedido', width: '4.75rem' },
    { label: 'Produto', width: 'minmax(12rem, 1.6fr)' },
    { label: 'Metal', width: '6.5rem' },
    { label: 'Data', width: '4.25rem' },
    { label: 'Valor', width: '6.75rem', align: 'right' },
    { label: 'Status', width: '8.25rem' },
];

const csvHeaders = ['Pedido', 'Produto', 'Criado por', 'Metal', 'Data', 'Status', 'Valor'];

function formatListDate(value) {
    if (!value) return '-';
    const text = String(value);
    const match = text.match(/^(\d{1,2})[/-](\d{1,2})/);
    if (match) {
        return `${match[1].padStart(2, '0')}/${match[2].padStart(2, '0')}`;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM') : text;
}

export default function Orders() {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [category, setCategory] = useState('');
    const [activeFilters, setActiveFilters] = useState({
        startDate: null,
        endDate: null,
        category: '',
    });

    const searchDebounced = useDebouncedValue(search, 300);

    const listFilters = {
        page,
        pageSize: ITEMS_PER_PAGE,
        search: searchDebounced,
        startDate: activeFilters.startDate ? dayjs(activeFilters.startDate).format('YYYY-MM-DD') : '',
        endDate: activeFilters.endDate ? dayjs(activeFilters.endDate).format('YYYY-MM-DD') : '',
        category: activeFilters.category,
    };

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['getOrders', listFilters],
        queryFn: () => getOrders(listFilters),
        staleTime: 1000 * 5,
        placeholderData: keepPreviousData,
    });

    const categoriesQuery = useQuery({ queryKey: ['getCategories'], queryFn: getCategories, staleTime: 1000 * 60 });
    const categories = useMemo(() => formatOptions(categoriesQuery.data), [categoriesQuery]);
    const orders = useMemo(() => data?.data?.data || [], [data]);
    const pagination = data?.data?.pagination;
    const serverPaged = Boolean(pagination);
    const total = pagination?.total ?? orders.length;
    const totalPages = Math.max(1, pagination?.totalPages ?? Math.ceil(total / ITEMS_PER_PAGE));
    const pageItems = serverPaged
        ? orders
        : orders.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    const handleFilter = () => {
        setPage(1);
        setActiveFilters({ startDate, endDate, category });
    };

    const handleCsv = async () => {
        const response = await getOrders({ ...listFilters, page: 1, all: 1 });
        const rows = response?.data?.data || [];
        handleDownloadOrders(startDate, endDate, rows, csvHeaders);
    };

    if (isLoading) {
        return <LoadingComponent />;
    }

    return (
        <CustomContainer>
            <Box sx={{ my: 4 }}>
                <PageHeader
                    title="Meus pedidos"
                    crumbs={['Meus pedidos']}
                    action={
                        <Button
                            component={Link}
                            to="/produtos"
                            variant="contained"
                            color="secondary"
                            startIcon={<FaPlus size={14} />}
                        >
                            Novo pedido
                        </Button>
                    }
                />
                <AppCard>
                    <Box sx={styles.toolbar}>
                        <SearchField
                            placeholder="Buscar por produto, data ou status…"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            sx={styles.search}
                        />
                        <Button
                            startIcon={<FaFilter />}
                            variant={filtersOpen ? 'contained' : 'outlined'}
                            color={filtersOpen ? 'secondary' : 'primary'}
                            onClick={() => setFiltersOpen((open) => !open)}
                            aria-expanded={filtersOpen}
                        >
                            Filtros
                        </Button>
                        <IconButton
                            aria-label="Baixar CSV"
                            onClick={handleCsv}
                            sx={styles.csv}
                        >
                            <FaFileCsv />
                        </IconButton>
                    </Box>

                    <Collapse in={filtersOpen}>
                        <Box sx={styles.filterPanel}>
                            <DatePicker
                                label="Data de início"
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                value={startDate}
                                onChange={setStartDate}
                                maxDate={endDate}
                            />
                            <DatePicker
                                label="Data de fim"
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                value={endDate}
                                onChange={setEndDate}
                                minDate={startDate}
                            />
                            <FormControl size="small" fullWidth>
                                <InputLabel id="categoria-select-label">Categoria</InputLabel>
                                <Select
                                    labelId="categoria-select-label"
                                    label="Categoria"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {categories.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="outlined" color="primary" onClick={handleFilter}>
                                Aplicar
                            </Button>
                        </Box>
                    </Collapse>

                    {orders.length === 0 ? (
                        total === 0 && !searchDebounced && !activeFilters.category && !activeFilters.startDate ? (
                            <EmptyState
                                icon={<FaRegImage />}
                                title="Nenhum pedido ainda"
                                description="Quando você encomendar uma joia, ela aparece aqui com o status da produção."
                                actionLabel="Ver produtos"
                                actionTo="/produtos"
                            />
                        ) : (
                            <EmptyState
                                icon={<FaRegImage />}
                                title="Nenhum pedido encontrado"
                                description="Tente ajustar a busca ou os filtros."
                            />
                        )
                    ) : (
                        <DataList columns={columns}>
                            {pageItems.map((order, index) => (
                                <DataRow key={order.id || index} to={`/pedido-detalhes/${order.id}`}>
                                    <DataCell sx={styles.id}>#{order.id}</DataCell>
                                    <DataCell>
                                        <Box sx={styles.prod}>
                                            <Avatar alt={order.name} src={order.image} sx={styles.avatar} variant="rounded">
                                                <FaRegImage size={14} />
                                            </Avatar>
                                            <Box sx={styles.prodText}>
                                                <Typography component="span" sx={styles.prodName}>{order.name}</Typography>
                                                {order.created_by && (
                                                    <Typography component="span" sx={styles.prodMeta}>{order.created_by}</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </DataCell>
                                    <DataCell sx={styles.meta}>{order.selected_metal || '-'}</DataCell>
                                    <DataCell sx={styles.num}>{formatListDate(order.ordered_at)}</DataCell>
                                    <DataCell align="right" sx={styles.value}>{moneyFormat(order.price)}</DataCell>
                                    <DataCell>
                                        <StatusChip
                                            color={orderStatusInfo[order.status]?.color}
                                            bg={orderStatusInfo[order.status]?.bg}
                                            label={orderStatusInfo[order.status]?.label}
                                        />
                                    </DataCell>
                                </DataRow>
                            ))}
                        </DataList>
                    )}

                    {totalPages > 1 && (
                        <Box sx={styles.paginationBox}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                color="primary"
                            />
                        </Box>
                    )}
                </AppCard>
            </Box>
        </CustomContainer>
    );
}

const styles = {
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        mb: 2,
    },
    search: {
        flex: 1,
        minWidth: 0,
    },
    csv: {
        color: 'text.secondary',
        width: 44,
        height: 44,
    },
    filterPanel: {
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr auto' },
        gap: 2,
        alignItems: 'center',
        mb: 3,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
    },
    id: {
        fontWeight: 600,
        fontSize: '0.8125rem',
        fontVariantNumeric: 'tabular-nums',
        color: 'text.secondary',
    },
    prod: {
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 0,
    },
    avatar: {
        width: 40,
        height: 40,
        bgcolor: 'background.default',
        color: 'text.secondary',
        flexShrink: 0,
    },
    prodText: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    prodName: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    prodMeta: {
        color: 'text.secondary',
        fontSize: '0.75rem',
        mt: 0.25,
    },
    meta: {
        fontSize: '0.8125rem',
        color: 'text.secondary',
    },
    num: {
        fontVariantNumeric: 'tabular-nums',
        fontSize: '0.8125rem',
        color: 'text.secondary',
    },
    value: {
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
        fontSize: '0.875rem',
    },
    paginationBox: {
        width: '100%',
        pt: 3,
        display: 'flex',
        justifyContent: 'flex-end',
    },
};
